/**
 * DrawingExport.ts
 * -----------------------------------------------------------------------------
 * Vector exporters for a `DrawingPage` — the same shape list Canvas2DRenderer
 * paints — so the 2D preview's toolbar can hand the user an SVG or a DXF of what
 * it is showing. (PNG comes from the live canvas instead; see
 * Canvas2DRenderer.ToPngDataUrl, which captures the actual view with its pan,
 * zoom, grid and theme.)
 *
 * Both writers consume the page as-is, so whatever the option toolbar currently
 * has switched on (dimensions / labels / values / units / text size) is already
 * baked into the shapes and comes out in the file.
 *
 * Coordinates: DrawingPage is page-space — origin top-left, y DOWN, units equal
 * model mm (see DrawingTypes). SVG shares that convention, so it copies straight
 * across; DXF is y-UP, so every y is negated on the way out.
 */

import {
  DimRecord,
  DrawingPage,
  DrawShape,
  HatchStyle,
  Rgb,
  TextShape,
  TextStyle,
  INK,
} from './DrawingTypes';

/** Axis-aligned page-space extent. */
export interface Bounds {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * The page's true drawing extent. `paperWidth/Height` are only the SIZE of the
 * content (Mesh2DExporter emits shapes around the model origin, which is usually
 * negative), so the shapes themselves are the authority; `fitRect` and the paper
 * rect are the fallbacks for a page that carries no drawable geometry.
 */
export function pageBounds(page: DrawingPage): Bounds {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  const grow = (x: number, y: number): void => {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  };

  for (const s of page.shapes) {
    switch (s.kind) {
      case 'line':
        grow(s.x1, s.y1);
        grow(s.x2, s.y2);
        break;
      case 'rect':
        grow(s.x, s.y);
        grow(s.x + s.w, s.y + s.h);
        break;
      case 'polygon':
      case 'polyline':
        for (const p of s.points) grow(p[0], p[1]);
        break;
      case 'circle':
        grow(s.cx - s.r, s.cy - s.r);
        grow(s.cx + s.r, s.cy + s.r);
        break;
      case 'text': {
        // Glyph advance isn't measurable without a canvas here; approximate the
        // run at 0.6 em/char so a label at the edge isn't clipped by the frame.
        const size = s.style?.size ?? 9;
        const w = s.text.length * size * 0.6;
        const anchor = s.style?.anchor ?? 'start';
        const x0 = anchor === 'middle' ? s.x - w / 2 : anchor === 'end' ? s.x - w : s.x;
        grow(x0, s.y - size);
        grow(x0 + w, s.y + size);
        break;
      }
    }
  }

  if (!isFinite(minX)) {
    const r = page.fitRect;
    if (r) return { x: r.x, y: r.y, w: Math.max(r.w, 1), h: Math.max(r.h, 1) };
    return {
      x: 0,
      y: 0,
      w: Math.max(page.paperWidth, 1),
      h: Math.max(page.paperHeight, 1),
    };
  }
  return {
    x: minX,
    y: minY,
    w: Math.max(maxX - minX, 1e-6),
    h: Math.max(maxY - minY, 1e-6),
  };
}

// =============================================================
//  SVG
// =============================================================

export interface SvgOptions {
  /** Invert shape lightness and paint a dark sheet, so the file matches a dark-
   *  mode preview. Off = the drawing as authored, on white. */
  dark?: boolean;
  /** Longest side of the emitted `width`/`height`, in CSS px. The viewBox stays
   *  in page units; this only sets the default display size (and with it the
   *  hairline width, which is one output pixel — the same constant-weight CAD
   *  line the on-screen renderer draws). */
  pixelSize?: number;
  /** Blank border around the drawing, as a fraction of its longest side. */
  margin?: number;
  /** Emit for embedding in a themed page rather than as a standalone file: no
   *  sheet rect, no `width`/`height`, and default ink as `currentColor` so the
   *  host stylesheet drives it. Shapes that carry a colour of their OWN keep it —
   *  those are layer colours and mean something. Used by `render2DSvg`, whose
   *  output has to follow a light/dark switch without being re-rendered. */
  inline?: boolean;
}

/** The whole drawing as a standalone SVG document. */
export function pageToSvg(page: DrawingPage, opts: SvgOptions = {}): string {
  const dark = opts.dark === true;
  const pixelSize = opts.pixelSize ?? 1600;
  const b = pageBounds(page);
  const pad = Math.max(b.w, b.h) * (opts.margin ?? 0.03);
  const vx = b.x - pad;
  const vy = b.y - pad;
  const vw = b.w + pad * 2;
  const vh = b.h + pad * 2;

  // Fit the longest side to pixelSize so the document has a sensible default
  // display size; `hair` is then exactly one output pixel in page units.
  const k = pixelSize / Math.max(vw, vh);
  const pxW = Math.max(1, Math.round(vw * k));
  const pxH = Math.max(1, Math.round(vh * k));
  const hair = vw / pxW;

  const inline = opts.inline === true;
  const out: string[] = [];
  out.push(
    inline
      ? `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${n(vx)} ${n(vy)} ${n(vw)} ${n(vh)}" ` +
          `preserveAspectRatio="xMidYMid meet">`
      : `<svg xmlns="http://www.w3.org/2000/svg" width="${pxW}" height="${pxH}" ` +
          `viewBox="${n(vx)} ${n(vy)} ${n(vw)} ${n(vh)}">`,
  );
  // The sheet. Inline, the host paints it (--canvas2d-bg) so the drawing sits on
  // the panel's own surface in either theme.
  if (!inline) {
    out.push(
      `<rect x="${n(vx)}" y="${n(vy)}" width="${n(vw)}" height="${n(vh)}" ` +
        `fill="${dark ? '#1e1e1e' : '#ffffff'}"/>`,
    );
  }
  // Hatch fills become <pattern> defs — a ruled concrete fill has to survive the
  // export, or a section comes out as an empty outline (see io/SvgImport, which
  // reads this same shape of pattern back in).
  const hatches = hatchDefs(page, dark, hair);
  if (hatches.defs) out.push(hatches.defs);

  out.push(
    '<g stroke-linecap="round" stroke-linejoin="round" ' +
      'font-family="Helvetica, Arial, sans-serif">',
  );
  for (const s of page.shapes) {
    const el = svgShape(s, dark, hair, hatches.idOf);
    if (el) out.push(el);
  }
  out.push('</g>');
  out.push('</svg>');
  const svg = out.join('\n');

  // Default ink -> currentColor, so a themed host restyles the drawing with CSS
  // instead of needing it re-rendered on every light/dark switch. Exact string,
  // taken from the same css() the shapes were written with, not a guess.
  return inline ? svg.split(css(INK, false)).join('currentColor') : svg;
}

/**
 * The <pattern> defs for every distinct hatch in the page, plus a lookup from a
 * HatchStyle to the id that draws it. One pattern per (spacing, angle, colour),
 * so a page of twenty hatched members carries one def, not twenty.
 */
function hatchDefs(
  page: DrawingPage,
  dark: boolean,
  hair: number,
): { defs: string; idOf: (h: HatchStyle) => string | null } {
  const ids = new Map<string, string>();
  const defs: string[] = [];
  const key = (h: HatchStyle): string =>
    `${n(h.spacing)}|${n(h.angle)}|${css(h.color ?? INK, dark)}`;

  for (const s of page.shapes) {
    const h = 'style' in s && s.style && 'hatch' in s.style ? s.style.hatch : undefined;
    if (!h || !(h.spacing > 0)) continue;
    const k = key(h);
    if (ids.has(k)) continue;
    const id = `hatch${ids.size}`;
    ids.set(k, id);
    defs.push(
      `<pattern id="${id}" width="${n(h.spacing)}" height="${n(h.spacing)}" ` +
        `patternTransform="rotate(${n(h.angle)})" patternUnits="userSpaceOnUse">` +
        `<line x1="0" y1="0" x2="0" y2="${n(h.spacing)}" ` +
        `stroke="${css(h.color ?? INK, dark)}" stroke-width="${n(hair)}"/></pattern>`,
    );
  }

  return {
    defs: defs.length ? `<defs>${defs.join('')}</defs>` : '',
    idOf: (h) => ids.get(key(h)) ?? null,
  };
}

/** A shape's fill: a hatch pattern when it has one, else its solid colour. */
function fillOrHatch(
  style: { fill?: Rgb | null; hatch?: HatchStyle } | undefined,
  dark: boolean,
  idOf: (h: HatchStyle) => string | null,
): string {
  const id = style?.hatch ? idOf(style.hatch) : null;
  return id ? ` fill="url(#${id})"` : fillAttrs(style?.fill, dark);
}

function svgShape(
  s: DrawShape,
  dark: boolean,
  hair: number,
  idOf: (h: HatchStyle) => string | null,
): string {
  switch (s.kind) {
    case 'line':
      return `<line x1="${n(s.x1)}" y1="${n(s.y1)}" x2="${n(s.x2)}" y2="${n(s.y2)}"${strokeAttrs(
        s.style?.color ?? INK,
        s.style?.width,
        s.style?.worldWidth,
        s.style?.dash,
        dark,
        hair,
      )}/>`;

    case 'rect':
      return `<rect x="${n(s.x)}" y="${n(s.y)}" width="${n(s.w)}" height="${n(s.h)}"${fillOrHatch(
        s.style,
        dark,
        idOf,
      )}${strokeAttrs(s.style?.stroke, s.style?.width, undefined, s.style?.dash, dark, hair)}/>`;

    case 'circle':
      return `<circle cx="${n(s.cx)}" cy="${n(s.cy)}" r="${n(s.r)}"${fillOrHatch(
        s.style,
        dark,
        idOf,
      )}${strokeAttrs(s.style?.stroke, s.style?.width, undefined, s.style?.dash, dark, hair)}/>`;

    case 'polyline':
      if (s.points.length < 2) return '';
      return `<polyline points="${pts(s.points)}" fill="none"${strokeAttrs(
        s.style?.color ?? INK,
        s.style?.width,
        s.style?.worldWidth,
        s.style?.dash,
        dark,
        hair,
      )}/>`;

    case 'polygon': {
      if (s.points.length < 2) return '';
      const holes = (s.holes ?? []).filter((h) => h.length >= 3);
      const stroke = strokeAttrs(
        s.style?.stroke,
        s.style?.width,
        undefined,
        s.style?.dash,
        dark,
        hair,
      );
      const fill = fillOrHatch(s.style, dark, idOf);
      if (holes.length === 0) {
        return `<polygon points="${pts(s.points)}"${fill}${stroke}/>`;
      }
      // Holes are even-odd subpaths, matching the canvas fill('evenodd').
      const d = [s.points, ...holes].map(ring).join(' ');
      return `<path d="${d}" fill-rule="evenodd"${fill}${stroke}/>`;
    }

    case 'text': {
      const st = s.style;
      const size = st?.size ?? 9;
      const anchor = st?.anchor ?? 'start';
      const valign = st?.valign ?? 'top';
      const rotate = st?.rotate ?? 0;
      // Same baseline arithmetic Canvas2DRenderer.DrawText uses, so the SVG and
      // the on-screen preview place their text identically. A rotated run is
      // centred on y (the canvas rotates about the anchor with a 'middle'
      // baseline), so it takes the 'middle' offset whatever its valign says.
      const dy = rotate
        ? size * 0.35
        : valign === 'bottom'
          ? 0
          : valign === 'middle'
            ? size * 0.35
            : size * 0.8;
      // Page y is down, so a positive (CCW) angle is a negative SVG rotation.
      const tf = rotate ? ` transform="rotate(${n(-rotate)} ${n(s.x)} ${n(s.y)})"` : '';
      const weight = st?.bold ? ' font-weight="700"' : '';
      return (
        `<text x="${n(s.x)}" y="${n(s.y + dy)}" font-size="${n(size)}"${weight}` +
        ` text-anchor="${anchor}" fill="${css(st?.color ?? INK, dark)}"${tf}>${esc(s.text)}</text>`
      );
    }
  }
}

function strokeAttrs(
  color: Rgb | null | undefined,
  width: number | undefined,
  worldWidth: number | undefined,
  dash: number[] | undefined,
  dark: boolean,
  hair: number,
): string {
  if (!color) return ' stroke="none"';
  // A world-width shape (a rebar at its diameter) keeps its true width; every
  // other line is the constant CAD hairline, scaled by its declared weight so
  // the drawing keeps its line-weight hierarchy.
  const w = worldWidth && worldWidth > 0 ? worldWidth : hair * ((width ?? 0.5) / 0.5);
  const d = dash && dash.length > 0 ? ` stroke-dasharray="${dash.map(n).join(' ')}"` : '';
  return ` stroke="${css(color, dark)}" stroke-width="${n(w)}"${d}`;
}

function fillAttrs(fill: Rgb | null | undefined, dark: boolean): string {
  return fill ? ` fill="${css(fill, dark)}"` : ' fill="none"';
}

function ring(points: ReadonlyArray<readonly [number, number]>): string {
  return (
    `M ${n(points[0][0])} ${n(points[0][1])} ` +
    points
      .slice(1)
      .map((p) => `L ${n(p[0])} ${n(p[1])}`)
      .join(' ') +
    ' Z'
  );
}

function pts(points: ReadonlyArray<readonly [number, number]>): string {
  return points.map((p) => `${n(p[0])},${n(p[1])}`).join(' ');
}

function css(c: Rgb, dark: boolean): string {
  const [r, g, b] = dark ? invertLightness(c) : c;
  const h = (v: number): string =>
    Math.max(0, Math.min(255, Math.round(v * 255)))
      .toString(16)
      .padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Trim float noise — page coords are rounded to 3dp upstream, and a compact
 *  file matters when a deck section runs to tens of thousands of shapes. */
function n(v: number): string {
  if (!isFinite(v)) return '0';
  return (Math.round(v * 1000) / 1000).toString();
}

// =============================================================
//  DXF
// =============================================================

/**
 * The STYLE table the drawing's lettering maps onto.
 *
 * A DXF never carries a font — it NAMES one, and the reader resolves it — so a
 * file with no STYLE table and no group 7 on its TEXT entities letters in
 * whatever the reader's default happens to be, usually a stroke font nothing
 * like the preview. These are the styles every TEXT entity points at.
 *
 * TrueType filenames rather than .shx, because that is what the drawing is
 * actually set in: the preview letters in the Helvetica/Arial stack and sets its
 * dimension values in a monospaced face (see TextStyle.mono — figures on a
 * drawing are tabular, so the digits of one dimension line up under the next).
 * A reader that cannot find one substitutes, which is the same fallback the
 * preview's own font stack has.
 */
const STYLES: ReadonlyArray<{ name: string; font: string }> = [
  { name: 'CSIB', font: 'arial.ttf' }, // the drawing's own lettering: names, stations, captions
  { name: 'CSIB-DIM', font: 'cour.ttf' }, // tabular figures: dimension values
  { name: 'CSIB-BOLD', font: 'arialbd.ttf' }, // headings and schedule captions
];

/** Name of the one dimension style the file defines. */
const DIMSTYLE = 'CSIB';

/** Which STYLE a text shape letters in — the same three-way choice
 *  Canvas2DRenderer makes when it picks a font stack. */
function styleOf(st: TextStyle | undefined): string {
  if (st?.mono) return 'CSIB-DIM';
  if (st?.bold) return 'CSIB-BOLD';
  return 'CSIB';
}

/**
 * DXF text height (group 40) is CAP height; a page's `size` is the EM size the
 * canvas and the SVG letter at. Arial's capitals are ~0.72 em, so a run written
 * straight across at its em size comes out about 40% taller in CAD than in the
 * preview — enough that a dimension value overran the gap broken in its own
 * dimension line for it. This is the one conversion between the two.
 */
const CAP_HEIGHT = 0.72;

/** Arrowhead length for DIMASZ, as a multiple of the value's CAP height — the
 *  same 0.25 em Mesh2DExporter drafts its heads at (its ArrowLength). */
const ARROW = 0.25 / CAP_HEIGHT;

/** Layer for the DIMENSION entities and the blocks behind them. */
const DIM_LAYER = 'DIMENSIONS';

const LAYERS: ReadonlyArray<string> = ['0', 'GEOMETRY', 'TEXT', DIM_LAYER];

/** Where a pass writes its entities. Inside a dimension's anonymous block
 *  everything goes on layer 0 and carries no colour of its own, which is how a
 *  dimension block is built: layer 0 in a block takes the layer of the entity
 *  that references it, so the DIMENSION in front of the block drives the whole
 *  picture — the way CAD expects to be able to. */
interface Target {
  geom: string;
  text: string;
  /** Suppress the per-entity colour group. */
  inherit: boolean;
}

const SHEET: Target = { geom: 'GEOMETRY', text: 'TEXT', inherit: false };
const IN_BLOCK: Target = { geom: '0', text: '0', inherit: true };

/**
 * The whole drawing as an ASCII DXF (AutoCAD R12).
 *
 * R12 rather than a later revision on purpose: it is the one flavour every CAD
 * reader accepts without argument, and nothing in a DrawingPage needs a newer
 * entity. The cost is per-entity colour by ACI index instead of true colour, and
 * filled shapes that come out as outlines — the 2D section view is a wireframe
 * anyway (see Mesh2DExporter, which strokes everything and fills nothing).
 *
 * Geometry lands on layer GEOMETRY, annotation on TEXT and dimensions on
 * DIMENSIONS, so the three can be frozen independently once the file is in CAD.
 * Text names a STYLE (see STYLES) rather than being left to the reader's
 * default, which is what puts the file in the face the drawing was set in.
 *
 * Dimensions come out as real DIMENSION entities, not as the loose lines and
 * text they are drawn from. Each one is written twice over: an anonymous block
 * holding the picture the preview showed — its witness lines, arrowheads, broken
 * rule and value, exactly as drafted — and a DIMENSION entity in front of it
 * carrying the measurement: the two points it spans, where its line sits, and
 * the value lettered on it. That is how AutoCAD itself writes an R12 dimension;
 * CAD shows the block, and picks, stretches and re-styles the entity. The
 * measurements come from `page.dims` (see DimRecord); a dimension the exporter
 * drafted without one behind it — an arc length along the alignment — still
 * exports as the lines that draw it, which is the honest answer for it.
 */
export function pageToDxf(page: DrawingPage): string {
  const b = pageBounds(page);
  const out: string[] = [];
  const g: Emit = (code, value) => {
    out.push(String(code));
    out.push(typeof value === 'number' ? nd(value) : value);
  };

  const { groups, claimed } = dimGroups(page);
  // The height the file's dimension values are lettered at, which is what the
  // DIMSTYLE is built around — taken from the values themselves rather than
  // assumed, so a dimension CAD re-draws comes back the size it was drafted.
  const txt = dimTextHeight(groups);

  // ---- HEADER ----
  g(0, 'SECTION');
  g(2, 'HEADER');
  g(9, '$ACADVER');
  g(1, 'AC1009');
  g(9, '$INSUNITS');
  g(70, '4'); // millimetres — page units are model mm (see DrawingTypes)
  g(9, '$DWGCODEPAGE');
  g(3, 'ANSI_1252'); // moot — every value is escaped to 7-bit ASCII; see dtext
  g(9, '$EXTMIN');
  g(10, b.x);
  g(20, -(b.y + b.h)); // page y is down; DXF y is up
  g(30, 0);
  g(9, '$EXTMAX');
  g(10, b.x + b.w);
  g(20, -b.y);
  g(30, 0);
  g(9, '$TEXTSTYLE');
  g(7, STYLES[0].name);
  g(9, '$TEXTSIZE');
  g(40, txt);
  g(9, '$DIMSTYLE');
  g(2, DIMSTYLE);
  g(9, '$DIMTXT');
  g(40, txt);
  g(9, '$DIMASZ');
  g(40, txt * ARROW);
  g(0, 'ENDSEC');

  // ---- TABLES: linetypes, layers, text styles, dimension style ----
  g(0, 'SECTION');
  g(2, 'TABLES');

  g(0, 'TABLE');
  g(2, 'LTYPE');
  g(70, '2');
  ltype(g, 'CONTINUOUS', 'Solid line', []);
  ltype(g, 'DASHED', 'Dashed __ __ __', [5, -2.5]);
  g(0, 'ENDTAB');

  g(0, 'TABLE');
  g(2, 'LAYER');
  g(70, String(LAYERS.length));
  for (const name of LAYERS) layer(g, name);
  g(0, 'ENDTAB');

  g(0, 'TABLE');
  g(2, 'STYLE');
  g(70, String(STYLES.length));
  for (const s of STYLES) style(g, s.name, s.font, txt);
  g(0, 'ENDTAB');

  g(0, 'TABLE');
  g(2, 'DIMSTYLE');
  g(70, '1');
  dimstyle(g, txt);
  g(0, 'ENDTAB');

  g(0, 'ENDSEC');

  // ---- BLOCKS: one anonymous block per dimension ----
  g(0, 'SECTION');
  g(2, 'BLOCKS');
  for (let i = 0; i < groups.length; i++) {
    const name = blockName(i);
    g(0, 'BLOCK');
    g(8, DIM_LAYER);
    g(2, name);
    g(70, '1'); // anonymous
    g(10, 0);
    g(20, 0);
    g(30, 0);
    g(3, name);
    g(1, ''); // no xref path
    const d = groups[i].d;
    for (let j = d.from; j < d.to; j++) dxfShape(g, page.shapes[j], IN_BLOCK);
    g(0, 'ENDBLK');
    g(8, DIM_LAYER);
  }
  g(0, 'ENDSEC');

  // ---- ENTITIES ----
  g(0, 'SECTION');
  g(2, 'ENTITIES');
  // Each dimension is written where its FIRST shape stood, so the file keeps the
  // order the drawing was drafted in; the rest of its shapes are skipped, being
  // in the block behind it already.
  const dimAt = new Map<number, number>();
  groups.forEach((gr, i) => dimAt.set(gr.d.from, i));
  for (let i = 0; i < page.shapes.length; i++) {
    const gi = dimAt.get(i);
    if (gi !== undefined) {
      dxfDimension(g, groups[gi], blockName(gi));
      continue;
    }
    if (claimed[i]) continue;
    dxfShape(g, page.shapes[i], SHEET);
  }
  g(0, 'ENDSEC');

  g(0, 'EOF');
  return out.join('\r\n') + '\r\n';
}

/** Writes one DXF group: an integer code line then its value line. Integer
 *  codes must be passed as strings so they aren't formatted as reals. */
type Emit = (code: number, value: string | number) => void;

/** A dimension paired with the drafted picture that becomes its block. */
interface DimGroup {
  d: DimRecord;
  /** The value's own text run, where the drafting emitted one. Its position is
   *  where the drafter chose to put the value — beside the line rather than in
   *  it, when there was no room — and CAD is told to keep it there. */
  label: TextShape | null;
}

/**
 * The page's dimensions paired with their shapes, and a flag per shape saying
 * whether some dimension has claimed it.
 *
 * A range that is malformed, or that overlaps one already taken, is dropped
 * rather than trusted: it would otherwise put the same shape in two blocks, or
 * in a block AND on the sheet. Its shapes then export as loose lines, which is
 * what every dimension did before the measurements were carried at all.
 */
function dimGroups(page: DrawingPage): { groups: DimGroup[]; claimed: Uint8Array } {
  const n = page.shapes.length;
  const claimed = new Uint8Array(n);
  const groups: DimGroup[] = [];

  for (const d of page.dims ?? []) {
    if (!Number.isInteger(d.from) || !Number.isInteger(d.to)) continue;
    if (!(d.from >= 0 && d.to > d.from && d.to <= n)) continue;
    if (!d.text) continue;

    let free = true;
    for (let i = d.from; i < d.to && free; i++) if (claimed[i]) free = false;
    if (!free) continue;

    let label: TextShape | null = null;
    for (let i = d.from; i < d.to; i++) {
      claimed[i] = 1;
      const s = page.shapes[i];
      if (s.kind === 'text') label = s;
    }
    groups.push({ d, label });
  }

  return { groups, claimed };
}

/** The cap height the file's dimension values are lettered at: the commonest
 *  size among them, so one outlier doesn't set the style for the sheet. 2.5 mm —
 *  the smallest ISO 3098 height — when there is nothing to measure. */
function dimTextHeight(groups: DimGroup[]): number {
  const counts = new Map<number, number>();
  for (const gr of groups) {
    const size = gr.label?.style?.size;
    if (size && size > 0) counts.set(size, (counts.get(size) ?? 0) + 1);
  }
  let best = 0;
  let bestN = 0;
  for (const [size, k] of counts) {
    if (k > bestN || (k === bestN && size > best)) {
      bestN = k;
      best = size;
    }
  }
  return best > 0 ? best * CAP_HEIGHT : 2.5;
}

/** R12 names a dimension's own block `*Dn`. */
function blockName(i: number): string {
  return `*D${i + 1}`;
}

function ltype(g: Emit, name: string, descr: string, pattern: number[]): void {
  g(0, 'LTYPE');
  g(2, name);
  g(70, '0');
  g(3, descr);
  g(72, '65');
  g(73, String(pattern.length));
  g(40, pattern.reduce((a, v) => a + Math.abs(v), 0));
  for (const p of pattern) g(49, p);
}

function layer(g: Emit, name: string): void {
  g(0, 'LAYER');
  g(2, name);
  g(70, '0');
  g(62, '7');
  g(6, 'CONTINUOUS');
}

/** One STYLE entry. A zero height leaves each TEXT entity carrying its own
 *  (group 40), which is what a drawing lettered at several sizes needs. */
function style(g: Emit, name: string, font: string, lastHeight: number): void {
  g(0, 'STYLE');
  g(2, name);
  g(70, '0');
  g(40, 0); // not a fixed-height style
  g(41, 1); // width factor
  g(50, 0); // no oblique
  g(71, '0'); // not mirrored, not upside down
  g(42, lastHeight);
  g(3, font);
  g(4, ''); // no big font
}

/**
 * The one dimension style, built around the height the sheet's values are
 * lettered at so a dimension CAD re-draws comes back the size it was drafted.
 *
 * The choices that matter are the ones the drafting already made: text INSIDE
 * the line and aligned WITH it (DIMTIH/DIMTOH 0, DIMTAD 0 — see
 * Mesh2DExporter.AddDimension2D, which breaks the rule around a centred value
 * and rotates it on a vertical dimension), the rule drawn between the witness
 * lines whatever the text does (DIMTOFL 1), and every colour BYBLOCK so the
 * DIMENSION entity's own layer drives the picture.
 */
function dimstyle(g: Emit, txt: number): void {
  g(0, 'DIMSTYLE');
  g(2, DIMSTYLE);
  g(70, '0');
  // The string variables, all empty, in the order AutoCAD writes them. Present
  // rather than omitted: a DIMSTYLE entry missing a field its reader expects is
  // the kind of thing that costs a whole file, and an empty value costs a line.
  g(3, ''); // DIMPOST — no prefix/suffix: the value is written as an override
  g(4, ''); // DIMAPOST
  g(5, ''); // DIMBLK — default arrowheads
  g(6, ''); // DIMBLK1
  g(7, ''); // DIMBLK2
  g(40, 1); // DIMSCALE
  g(41, txt * ARROW); // DIMASZ
  g(42, txt * 0.75); // DIMEXO — witness line held off the feature
  g(43, txt * 3); // DIMDLI — baseline spacing
  g(44, txt * 0.75); // DIMEXE — witness line run past the dimension line
  g(45, 0); // DIMRND — no rounding
  g(46, 0); // DIMDLE
  g(47, 0); // DIMTP
  g(48, 0); // DIMTM
  g(140, txt); // DIMTXT
  g(141, txt * 0.4); // DIMCEN
  g(142, 0); // DIMTSZ — arrowheads, not ticks
  g(143, 0.03937007874); // DIMALTF
  g(144, 1); // DIMLFAC — the value is an override, so nothing is scaled
  g(145, 0); // DIMTVP
  g(146, 1); // DIMTFAC
  g(147, txt * 0.4); // DIMGAP
  g(71, '0'); // DIMTOL
  g(72, '0'); // DIMLIM
  g(73, '0'); // DIMTIH — inside text aligned with the dimension line
  g(74, '0'); // DIMTOH — outside text too
  g(75, '0'); // DIMSE1
  g(76, '0'); // DIMSE2
  g(77, '0'); // DIMTAD — value centred in the line, not sitting above it
  g(78, '1'); // DIMZIN — suppress a leading zero
  g(170, '0'); // DIMALT
  g(171, '2'); // DIMALTD
  g(172, '1'); // DIMTOFL
  g(173, '0'); // DIMSAH
  g(174, '0'); // DIMTIX
  g(175, '0'); // DIMSOXD
  g(176, '0'); // DIMCLRD — BYBLOCK
  g(177, '0'); // DIMCLRE
  g(178, '0'); // DIMCLRT
}

/**
 * One linear DIMENSION entity: the measurement, in front of the block that
 * pictures it.
 *
 * Group 70 is a rotated/horizontal/vertical linear dimension (0) plus 32, which
 * says the block belongs to this dimension alone. Only those two: the bit that
 * pins the text to a user-defined position arrived in R13, and an R12 reader is
 * entitled to reject a value it has no meaning for. The block already shows the
 * value where the drafting put it, and 11/21 says where that was.
 *
 * The value goes out as a text OVERRIDE (group 1). Page units are PLOT
 * millimetres, so a view drawn at 1:200 on a composed sheet would otherwise come
 * back measuring a two-hundredth of the structure.
 */
function dxfDimension(g: Emit, gr: DimGroup, block: string): void {
  const d = gr.d;
  g(0, 'DIMENSION');
  g(8, DIM_LAYER);
  g(2, block);
  g(3, DIMSTYLE);
  g(10, d.lineX);
  g(20, -d.lineY);
  g(30, 0);
  g(11, gr.label ? gr.label.x : (d.x1 + d.x2) / 2);
  g(21, -(gr.label ? gr.label.y : d.lineY));
  g(31, 0);
  g(70, '32');
  g(1, dtext(d.text));
  g(13, d.x1);
  g(23, -d.y1);
  g(33, 0);
  g(14, d.x2);
  g(24, -d.y2);
  g(34, 0);
  // 15/25/35 and 16/26/36 belong to the dimension types this is not (angular,
  // radial), and R12 writes them zeroed rather than absent.
  g(15, 0);
  g(25, 0);
  g(35, 0);
  g(16, 0);
  g(26, 0);
  g(36, 0);
  g(50, d.vertical ? 90 : 0); // angle of the dimension line
}

function dxfShape(g: Emit, s: DrawShape, t: Target): void {
  switch (s.kind) {
    case 'line':
      dxfHead(g, 'LINE', t.geom, s.style?.color ?? INK, s.style?.dash, t);
      g(10, s.x1);
      g(20, -s.y1);
      g(30, 0);
      g(11, s.x2);
      g(21, -s.y2);
      g(31, 0);
      break;

    case 'rect':
      dxfPolyline(
        g,
        [
          [s.x, s.y],
          [s.x + s.w, s.y],
          [s.x + s.w, s.y + s.h],
          [s.x, s.y + s.h],
        ],
        true,
        s.style?.stroke ?? s.style?.fill ?? INK,
        s.style?.dash,
        t,
      );
      break;

    case 'circle':
      dxfHead(g, 'CIRCLE', t.geom, s.style?.stroke ?? s.style?.fill ?? INK, s.style?.dash, t);
      g(10, s.cx);
      g(20, -s.cy);
      g(30, 0);
      g(40, s.r);
      break;

    case 'polyline':
      dxfPolyline(g, s.points, false, s.style?.color ?? INK, s.style?.dash, t);
      break;

    case 'polygon': {
      // Boundaries only: a hatch fill (concrete) is deliberately NOT exploded into
      // line entities here. CAD wants the closed polyline so it can apply its own
      // HATCH with its own pattern and scale, and a few thousand loose segments in
      // its place would be worse than nothing.
      const c = s.style?.stroke ?? s.style?.fill ?? INK;
      dxfPolyline(g, s.points, true, c, s.style?.dash, t);
      for (const h of s.holes ?? []) {
        if (h.length >= 3) dxfPolyline(g, h, true, c, s.style?.dash, t);
      }
      break;
    }

    case 'text': {
      const st = s.style;
      const anchor = st?.anchor ?? 'start';
      const valign = st?.valign ?? 'top';
      dxfHead(g, 'TEXT', t.text, st?.color ?? INK, undefined, t);
      g(10, s.x);
      g(20, -s.y);
      g(30, 0);
      g(40, (st?.size ?? 9) * CAP_HEIGHT);
      g(1, dtext(s.text));
      // Negating y already turns the drawing the right way up, which makes the
      // page's CCW angle a CCW DXF angle too — no sign flip here.
      if (st?.rotate) g(50, st.rotate);
      // The STYLE this run letters in. Without it the text falls back on whatever
      // the reader's default is, which is not the face the drawing was set in.
      g(7, styleOf(st));
      // 72/73 justification: the 11/21 alignment point takes over from 10/20
      // whenever either is non-zero, so both points are written. Group order
      // follows the DXF reference, which puts 73 after the alignment point.
      g(72, anchor === 'middle' ? '1' : anchor === 'end' ? '2' : '0');
      g(11, s.x);
      g(21, -s.y);
      g(31, 0);
      g(73, valign === 'bottom' ? '0' : valign === 'middle' ? '2' : '3');
      break;
    }
  }
}

function dxfPolyline(
  g: Emit,
  points: ReadonlyArray<readonly [number, number]>,
  closed: boolean,
  color: Rgb | null,
  dash: number[] | undefined,
  t: Target,
): void {
  if (points.length < 2) return;
  dxfHead(g, 'POLYLINE', t.geom, color ?? INK, dash, t);
  g(66, '1'); // vertices follow
  g(10, 0);
  g(20, 0);
  g(30, 0);
  g(70, closed ? '1' : '0');
  for (const p of points) {
    dxfHead(g, 'VERTEX', t.geom, null, undefined, t);
    g(10, p[0]);
    g(20, -p[1]);
    g(30, 0);
  }
  g(0, 'SEQEND');
  g(8, t.geom);
}

function dxfHead(
  g: Emit,
  type: string,
  layerName: string,
  color: Rgb | null,
  dash: number[] | undefined,
  t: Target,
): void {
  g(0, type);
  g(8, layerName);
  // Inside a dimension's block the entity is left with no colour of its own, so
  // it inherits — which is what makes the DIMENSION in front of it govern.
  if (!t.inherit && color) g(62, String(nearestAci(color)));
  if (dash && dash.length > 0) g(6, 'DASHED');
}

/**
 * The non-ASCII characters a drawing's lettering picks up, written the way a
 * drawing writes them.
 *
 *  - The engineering symbols become AutoCAD's own control codes, which every
 *    CAD reader draws as the symbol they stand for.
 *  - The typographic ones become their ASCII equivalents: on a drawing a hyphen
 *    IS the dash, and "m2" is how a unit is lettered.
 */
const ASCII: ReadonlyMap<number, string> = new Map([
  [0x00d8, '%%c'], // O-slash: diameter, as a rebar size is written
  [0x00b0, '%%d'], // degree: a skew angle
  [0x00b1, '%%p'], // plus/minus
  [0x2014, '-'], // em dash: the separator in a view title
  [0x2013, '-'], // en dash
  [0x2212, '-'], // minus sign
  [0x00d7, 'x'], // multiplication sign
  [0x00a0, ' '], // no-break space
  [0x2018, "'"],
  [0x2019, "'"],
  [0x201c, '"'],
  [0x201d, '"'],
  [0x00b2, '2'], // superscript two
  [0x00b3, '3'],
  [0x2026, '...'],
]);

/**
 * A string as a DXF text value, in 7-bit ASCII.
 *
 * An R12 file carries no encoding of its own — a reader takes its text as the
 * drawing's ANSI code page — so a UTF-8 byte arrives as two or three of the
 * wrong characters: the em dash in "Cap beam — Elevation and Section" came back
 * out of CAD as `â€"`. Rather than gamble on a code page, the characters a
 * drawing actually uses are written as ASCII (see ASCII above) and the file
 * stays 7-bit whatever opens it.
 *
 * Anything left over falls back to `\\U+XXXX`, the escape AutoCAD writes for a
 * character outside the code page: AutoCAD and BricsCAD decode it, and a reader
 * that does not at least shows what was meant rather than mojibake. Above the
 * BMP there is no such escape, and nothing on a drawing needs one.
 */
function dtext(s: string): string {
  let out = '';
  for (const ch of s) {
    const cp = ch.codePointAt(0) ?? 0;
    if (cp >= 32 && cp < 127) out += ch;
    else {
      const ascii = ASCII.get(cp);
      if (ascii !== undefined) out += ascii;
      else if (cp > 0xffff || cp < 32) out += '?';
      else out += `\\U+${cp.toString(16).toUpperCase().padStart(4, '0')}`;
    }
  }
  return out;
}

/** DXF reals: enough precision for mm geometry, without 17-digit noise. */
function nd(v: number): string {
  if (!isFinite(v)) return '0.0';
  const r = Math.round(v * 1e6) / 1e6;
  return Number.isInteger(r) ? `${r}.0` : String(r);
}

/** Basic ACI palette (index → RGB 0..255) used to approximate a shape colour.
 *  R12 has no true-colour group, and these are what every reader renders the
 *  same way. */
const ACI: ReadonlyArray<readonly [number, number, number, number]> = [
  [1, 255, 0, 0],
  [2, 255, 255, 0],
  [3, 0, 255, 0],
  [4, 0, 255, 255],
  [5, 0, 0, 255],
  [6, 255, 0, 255],
  [8, 128, 128, 128],
  [9, 192, 192, 192],
];

/** Nearest ACI index for an `Rgb` triple. Near-black and near-white both map to
 *  7, which CAD draws as the inverse of the sheet — so the dark ink a drawing is
 *  authored in stays visible whatever background the user has set. */
function nearestAci(c: Rgb): number {
  const r = c[0] * 255;
  const g = c[1] * 255;
  const b = c[2] * 255;
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  const sat = Math.max(c[0], c[1], c[2]) - Math.min(c[0], c[1], c[2]);
  if (sat < 0.12 && (lum < 0.25 || lum > 0.9)) return 7;
  let best = 7;
  let bestD = Infinity;
  for (const [idx, ar, ag, ab] of ACI) {
    const d = (r - ar) ** 2 + (g - ag) ** 2 + (b - ab) ** 2;
    if (d < bestD) {
      bestD = d;
      best = idx;
    }
  }
  return best;
}

// =============================================================
//  Shared colour helpers (dark-mode SVG)
// =============================================================

/** Invert a colour's lightness while keeping hue + saturation — the same
 *  transform Canvas2DRenderer applies in dark mode, kept here so neither module
 *  reaches into the other's internals. */
function invertLightness(c: Rgb): Rgb {
  const [h, s, l] = rgbToHsl(c);
  return hslToRgb(h, s, 1 - l);
}

function rgbToHsl([r, g, b]: Rgb): [number, number, number] {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return [0, 0, l];
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h, s, l];
}

function hslToRgb(h: number, s: number, l: number): Rgb {
  if (s === 0) return [l, l, l];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [hue2rgb(p, q, h + 1 / 3), hue2rgb(p, q, h), hue2rgb(p, q, h - 1 / 3)];
}

function hue2rgb(p: number, q: number, t: number): number {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
}
